// Fio de discussão (encaminhamento de projeto ou assunto avulso): histórico de
// mensagens vai-e-volta, caixa para comentar, e ações de Encerrar (dono/admin),
// Reabrir (admin) e Excluir (dono/admin).
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { uploadEvidencia } from '../lib/photos';
import { loadPeople, type Person } from '../lib/notifications';
import {
  addDiscussionMentions,
  addMessage,
  deleteDiscussion,
  finalizeDiscussion,
  reopenDiscussion,
  type Discussion,
} from '../lib/encaminhamentos';

export function DiscussionThread({
  d,
  userId,
  fullName,
  isAdmin,
  anchorN,
  onChanged,
  showProjectLink = true,
  onTirarPauta,
}: {
  d: Discussion;
  userId: string | null;
  fullName: string | null;
  isAdmin: boolean;
  anchorN?: number;
  onChanged: () => void;
  showProjectLink?: boolean;
  // Só na reunião: tira este fio da pauta de hoje (vai para "Fora da pauta").
  onTirarPauta?: () => void;
}) {
  const nav = useNavigate();
  const [body, setBody] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [people, setPeople] = useState<Person[]>([]);
  const [mentions, setMentions] = useState<string[]>([]);

  useEffect(() => {
    loadPeople(d.orgId).then(setPeople);
  }, [d.orgId]);

  const toggleMention = (id: string) =>
    setMentions((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const owner = d.createdById === userId;
  const canFinalize = d.status === 'aberto' && (owner || isAdmin);
  const canReopen = d.status === 'encerrado' && isAdmin;
  const canDelete = owner || isAdmin;

  async function comentar() {
    const hasMention = mentions.length > 0;
    if (!body.trim() && !file && !hasMention) return;
    setBusy(true);
    let attachmentUrl: string | null = null;
    let attachmentName: string | null = null;
    if (file && d.productId) {
      const up = await uploadEvidencia(d.orgId, d.productId, anchorN ?? 1, file);
      if (up.error) {
        alert(up.error);
        setBusy(false);
        return;
      }
      attachmentUrl = up.url ?? null;
      attachmentName = up.name ?? null;
    }
    // Nomes dos marcados que ainda não eram participantes (para a mensagem/cabeçalho).
    const novosNomes = people
      .filter((p) => mentions.includes(p.id) && !d.participantIds.includes(p.id))
      .map((p) => p.name ?? '—');
    const fallbackBody =
      hasMention && novosNomes.length
        ? `📌 Direcionado a ${novosNomes.join(', ')} para responder.`
        : attachmentName
          ? `(anexo) ${attachmentName}`
          : '';
    const { error } = await addMessage({
      discussion: d,
      authorId: userId,
      authorName: fullName,
      body: body.trim() || fallbackBody,
      attachmentUrl,
      attachmentName,
    });
    if (error) {
      setBusy(false);
      return alert(error);
    }
    // Direciona o "a fazer": marca as pessoas no fio e notifica.
    if (hasMention) {
      const existentes = (d.assignedName ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
      const allNames = [...new Set([...existentes, ...novosNomes])].join(', ');
      await addDiscussionMentions({
        discussion: d,
        newIds: mentions,
        allNames,
        fromId: userId,
        fromName: fullName,
      });
    }
    setBusy(false);
    setBody('');
    setFile(null);
    setMentions([]);
    onChanged();
  }

  async function act(fn: () => Promise<{ error?: string }>) {
    setBusy(true);
    const { error } = await fn();
    setBusy(false);
    if (error) return alert(error);
    onChanged();
  }

  return (
    <div className={`disc ${d.status === 'encerrado' ? 'disc-fechada' : ''}`}>
      <div className="disc-head">
        <div className="disc-title">
          {d.kind === 'assunto' ? '🗂️' : '📌'} <b>{d.title}</b>
          {showProjectLink && d.productId && (
            <button
              className="linklike"
              style={{ marginLeft: 6, fontWeight: 600 }}
              onClick={() => nav(`/produto/${d.productId}`)}
              title="Abrir o projeto"
            >
              {d.productName ? `${d.productName} ›` : 'abrir projeto ›'}
            </button>
          )}
          {d.assignedName && (
            <span className="muted">
              {' '}· {d.participantIds.length > 1 ? '👥 entre' : 'resp.'} {d.assignedName}
            </span>
          )}
          <span className={`seal ${d.status === 'encerrado' ? 'ok' : 'pend'}`}>
            {d.status === 'encerrado' ? 'encerrado' : 'aberto'}
          </span>
        </div>
        <div className="disc-actions">
          {onTirarPauta && (
            <button
              className="btn-ghost btn-sm"
              disabled={busy}
              onClick={onTirarPauta}
              title="Tirar da pauta de hoje (continua aberto; some do topo e vai para 'Fora da pauta')"
            >
              ✕ tirar da pauta
            </button>
          )}
          {canFinalize && (
            <button className="btn-primary btn-sm" disabled={busy} onClick={() => act(() => finalizeDiscussion(d.id))}>
              Encerrar
            </button>
          )}
          {canReopen && (
            <button className="btn-ghost btn-sm" disabled={busy} onClick={() => act(() => reopenDiscussion(d))}>
              Reabrir
            </button>
          )}
          {canDelete && (
            <button
              className="linklike"
              onClick={() => {
                if (confirm('Excluir esta discussão e todo o histórico?')) act(() => deleteDiscussion(d.id));
              }}
            >
              excluir
            </button>
          )}
        </div>
      </div>

      <div className="disc-msgs">
        {d.messages.length === 0 ? (
          <p className="muted" style={{ fontSize: 12.5, margin: '4px 0' }}>Sem mensagens ainda.</p>
        ) : (
          d.messages.map((m) => (
            <div className={`disc-msg ${m.authorId === userId ? 'mine' : ''}`} key={m.id}>
              <div className="disc-msg-meta">
                <b>{m.authorName ?? '—'}</b> ·{' '}
                {new Date(m.createdAt).toLocaleString('pt-BR', {
                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
                })}
              </div>
              {m.body && <div className="disc-msg-body">{m.body}</div>}
              {m.attachmentUrl && (
                <a className="note-attach" href={m.attachmentUrl} target="_blank" rel="noreferrer">
                  📎 {m.attachmentName || m.attachmentUrl}
                </a>
              )}
            </div>
          ))
        )}
      </div>

      {d.status === 'aberto' && (
        <div className="disc-reply">
          <textarea
            className="note-textarea"
            rows={2}
            placeholder="Escreva uma resposta / comentário…"
            value={body}
            onChange={(e) => setBody(e.target.value)}
          />
          {people.length > 0 && (
            <div className="note-mentions">
              <span className="muted" style={{ fontSize: 12 }}>Direcionar a:</span>
              {people.map((p) => (
                <button
                  key={p.id}
                  type="button"
                  className={`chip ${mentions.includes(p.id) ? 'on' : ''}`}
                  onClick={() => toggleMention(p.id)}
                  title="Marca a pessoa no fio e direciona o 'a fazer' para ela responder"
                >
                  @{p.name}
                </button>
              ))}
            </div>
          )}
          <div className="reuniao-actions">
            {d.productId && (
              <label className="note-clip" title="Anexar arquivo">
                📎 {file ? file.name : 'Anexo'}
                <input type="file" style={{ display: 'none' }} onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
              </label>
            )}
            <button
              className="btn-primary btn-sm"
              disabled={busy || (!body.trim() && !file && mentions.length === 0)}
              onClick={comentar}
            >
              {busy ? 'Enviando…' : mentions.length > 0 && !body.trim() && !file ? 'Direcionar' : 'Comentar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
